'use client';
import * as React from 'react';
import { DOWNLOAD_COOKIE, readDownloadSignal } from '@/lib/download-signal';

/** The longest a control stays busy when no signal can arrive: a presigned URL, or a response that never comes. */
const SIGNAL_CAP_MS = 90_000;
/** A cross-origin download cannot report back; the control is held this long against a second press. */
const COURTESY_HOLD_MS = 1500;

export interface FileDownload {
  /** The key of the download being started, or null when none is. */
  pending: string | null;
  /** Start `href` as a browser download. `key` names the control, so that one alone shows the spinner. */
  start: (key: string, href: string) => void;
}

/**
 * A native download with feedback (docs/10-ux-audit.md · UX-47).
 *
 * People pressed "Download album" and nothing changed for as long as the server took to answer, so
 * they pressed again — seven copies of one PDF were in flight at once. The control now spins from
 * the press until the download has actually begun, and ignores presses meanwhile. For a same-origin
 * media URL "begun" is exact: the request carries a token that the media route echoes back as a
 * cookie with its first bytes (`lib/download-signal.ts`). A presigned URL says nothing, so it gets
 * a short hold instead.
 */
export function useFileDownload(options: { onError?: () => void } = {}): FileDownload {
  const [pending, setPending] = React.useState<string | null>(null);
  // Two presses in the same tick share one render closure; the ref is already true for the second.
  const busy = React.useRef(false);
  const stop = React.useRef<() => void>(() => undefined);
  const onError = React.useRef(options.onError);
  onError.current = options.onError;

  React.useEffect(() => () => stop.current(), []);

  const settle = React.useCallback(() => {
    stop.current();
    stop.current = () => undefined;
    busy.current = false;
    setPending(null);
  }, []);

  const start = React.useCallback(
    (key: string, href: string) => {
      if (busy.current) return;
      busy.current = true;
      setPending(key);

      const url = new URL(href, window.location.href);
      const token = url.origin === window.location.origin ? Math.random().toString(36).slice(2, 12) : null;
      if (token) url.searchParams.set('dl', token);

      const anchor = document.createElement('a');
      anchor.href = url.toString();
      // Empty on purpose: the server names the file (Content-Disposition), as it always has.
      anchor.download = '';
      anchor.rel = 'noopener';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();

      if (!token) {
        const hold = setTimeout(settle, COURTESY_HOLD_MS);
        stop.current = () => clearTimeout(hold);
        return;
      }
      const started = Date.now();
      const poll = setInterval(() => {
        const signal = readDownloadSignal(document.cookie, token);
        if (!signal && Date.now() - started < SIGNAL_CAP_MS) return;
        document.cookie = `${DOWNLOAD_COOKIE}=; Path=/; Max-Age=0`;
        settle();
        if (signal === 'error') onError.current?.();
      }, 250);
      stop.current = () => clearInterval(poll);
    },
    [settle],
  );

  return { pending, start };
}
